<template lang="html">
  <section class="game-settings">
    <h1 class="title">Settings</h1>

    <section v-if="currentUser">
      <p>
        Logged in as <b>{{ currentUser.email }}</b>
      </p>
    </section>

    <section class="stats">
      <h2 class="stats-title">Stats</h2>
      <ul>
        <SessionMovesStats tag="li" />
        <li v-if="currentUser">
          Correct moves (all time): {{ currentUser.correctMovesCount }}/{{
            currentUser.allMovesCount
          }}
          (<b>{{ currentUser.correctMovesPercentage }}%</b>)
        </li>
      </ul>
      <div v-if="currentUser">
        <a role="button" @click="handleClickResetStats">Reset Stats</a>
      </div>
      <div v-else>
        <router-link :to="{ name: 'login' }">Log in</router-link> to track more
        play stats.
      </div>
    </section>

    <form class="game-settings-form" @submit.prevent="handleSubmit">
      <div class="form-content">
        <fieldset class="form-row">
          <Checkbox
            v-model="gameSettings.hideCardTotals"
            class="inline-form-row"
            label="Hide card totals"
          />

          <Checkbox
            v-model="gameSettings.autoDeclineInsurance"
            class="inline-form-row"
            label="Auto-decline insurance"
          />

          <Checkbox
            v-model="gameSettings.checkDeviations"
            class="inline-form-row"
            label="Check deviations"
            :disabled="gameSettings.mode === 'deviations'"
          />
        </fieldset>

        <fieldset class="form-row">
          <Checkbox
            v-model="gameSettings.allowLateSurrender"
            class="inline-form-row"
            label="Allow late surrender"
          />

          <Checkbox
            v-model="gameSettings.hitSoft17"
            class="form-row inline-form-row"
            label="Dealer hits soft 17"
          />

          <div class="form-row">
            <label for="deck-count">Deck count</label>
            <select id="deck-count" v-model="gameSettings.deckCount">
              <option
                v-for="(name, count) in deckCounts"
                :key="count"
                :value="count"
              >
                {{ name }}
              </option>
            </select>
          </div>

          <div class="form-row">
            <label for="game-mode">Game mode</label>
            <select id="game-mode" v-model="gameSettings.mode">
              <option v-for="(name, key) in gameModes" :key="key" :value="key">
                {{ name }}
              </option>
            </select>

            <ol class="game-mode-explanations">
              <li v-for="({ name, detail }, key) in gameModeDetails" :key="key">
                <b>{{ name }}</b
                >: {{ detail }}
              </li>
            </ol>
          </div>
        </fieldset>

        <LoadingButton
          class="form-row"
          button-text="Save Settings"
          :is-loading="isUpdating"
        />
      </div>
    </form>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import { startCase } from 'lodash';
import { GameMode } from '@blackjacktrainer/blackjack-simulator';

import { deckCounts, gameModeDetails } from '@/constants';
import entityShowView from '@/mixins/entity-show-view';
import Move from '@/models/Move';
import User from '@/models/User';
import LoadingButton from '@/components/LoadingButton';
import Checkbox from '@/components/Checkbox';
import SessionMovesStats from '@/components/SessionMovesStats';

export default {
  name: 'GameSettings',

  components: {
    LoadingButton,
    Checkbox,
    SessionMovesStats,
  },

  mixins: [entityShowView],

  data() {
    return {
      deckCounts,
      gameModeDetails,

      gameModes: {
        default: 'Default',
        pairs: 'Pairs',
        uncommon: 'Uncommon',
        deviations: 'Deviations',
      },

      entityClass: User,
    };
  },

  computed: {
    ...mapGetters(['currentUser', 'settings']),

    // We use a copy of the vuex settings state to prevent errors about directly
    // modifying store state when the user interacts with UI elements.
    localSettingsCopy() {
      return Object.assign({}, this.settings);
    },

    gameSettings() {
      return this.entity.settings ?? this.localSettingsCopy;
    },

    entityIdentifier() {
      return this.$auth.user()?.id.toString() ?? 'new';
    },
  },

  watch: {
    'gameSettings.mode'(mode) {
      const updates = {
        mode: GameMode[startCase(mode)],
      };

      if (mode === 'deviations') {
        this.$set(this.entity.settings, 'checkDeviations', true);
        updates.checkDeviations = true;
      }

      this.$store.dispatch('updateSettings', updates);
    },
  },

  mounted() {
    this.updateStats();
  },

  methods: {
    async handleSubmit() {
      if (this.currentUser) {
        await this.createOrUpdateEntity();
      } else {
        // TODO: Persist to local storage?
        this.$store.dispatch('updateSettings', this.localSettingsCopy);
      }

      this.$toasted.show('Saved settings', { type: 'success' });
      this.$router.push({ name: 'game' });
    },

    transformEntityData(entityData) {
      entityData.settings.deckCount = parseInt(entityData.settings.deckCount);

      return entityData;
    },

    setupHeaderContext() {
      this.$store.dispatch('setHeaderContext', {
        componentName: 'HeaderNavBack',
        props: {
          prevRoute: { name: 'game' },
        },
      });
    },

    updateStats() {
      if (!this.currentUser) {
        return;
      }

      // HACK: Update the user since stats are stored in the user record.
      User.api().get(`/${User.entity}/${this.currentUser.id}`);
    },

    handleClickResetStats() {
      if (window.confirm('Are you sure you want to permanently reset stats?')) {
        Move.api().reset();
        Move.deleteAll();
        this.updateStats();
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/mixins/app-width';
@import '@/styles/mixins/block-form';
@import '@/styles/mixins/unstyled-list';

.game-settings {
  @include app-width;
}

.game-settings-form {
  @include block-form;

  margin-top: map-get($app, 'spacing') * 2;
  width: 100%;
  max-width: map-get($breakpoints, 'phone');
}

.game-mode-explanations {
  @include unstyled-list;

  flex-direction: column;
  font-size: 0.8em;
  margin-top: map-get($app, 'spacing');
}

.check-top-n-deviations {
  margin: 0 0.75em;
}

.stats {
  background-color: map-get($colors, 'faded-background-1x');
  border-radius: map-get($app, 'border-radius');
  padding: map-get($app, 'spacing');
}

.stats-title {
  margin-top: 0;
}
</style>
